import { Globe } from 'lucide-react';
import { forwardRef, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getNativeDisplayLanguageName } from '@/constants/display-language-native-names';
import { cn } from '@/lib/cn';
import { enumToDescription } from '@/utils/enumToDescription';
import { Combobox, type ComboboxItem } from '@heidi/design-system/combobox';
import { LanguageCode } from '@heidi/schema/ml-scribe';

import { displayLanguageSelectMessages } from './display-language-select.messages';
import { type DisplayLanguageSelectProps } from './display-language-select.types';

export const DisplayLanguageSelect = forwardRef<
  HTMLButtonElement,
  DisplayLanguageSelectProps
>(
  (
    {
      setValue,
      value,
      availableLanguages,
      className,
      contentClassName,
      disabled,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
    },
    ref
  ) => {
    const intl = useIntl();

    const items = useMemo<ComboboxItem[]>(
      () =>
        availableLanguages.map((languageCode) => ({
          value: languageCode,
          label: getNativeDisplayLanguageName(languageCode),
          // Search targets: native label (via searchBy="label") plus these
          // keywords — English name and the ISO code — so "한국어", "Korean",
          // and "ko" all resolve to the same option.
          keywords: [enumToDescription[languageCode], languageCode],
        })),
      [availableLanguages]
    );

    return (
      <Combobox
        triggerRef={ref}
        data-testid="display-language-select"
        className={cn('w-full', className)}
        contentClassName={contentClassName}
        items={items}
        searchBy="label"
        value={value}
        onValueChange={(next) => {
          if (next !== null) {
            setValue(next as LanguageCode);
          }
        }}
        clearable={false}
        disabled={disabled}
        placeholder={getNativeDisplayLanguageName(LanguageCode.en)}
        emptyText={intl.formatMessage(displayLanguageSelectMessages.noResultsFound)}
        leadingIcon={<Globe />}
        error={ariaInvalid === true || ariaInvalid === 'true'}
        buttonProps={{
          id,
          'aria-describedby': ariaDescribedBy,
          'aria-invalid': ariaInvalid,
        }}
      />
    );
  }
);
DisplayLanguageSelect.displayName = 'DisplayLanguageSelect';

export default DisplayLanguageSelect;
